import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { motion } from 'framer-motion';
import { Banknote, FileText, Mail, Repeat, ShieldCheck, } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
/** Snapshot highlights — top operational buckets from the master list. */
const CATEGORIES = [
    {
        key: 'Payment',
        icon: Banknote,
        iconClass: 'bg-emerald-100 text-emerald-700',
        barClass: 'bg-emerald-500',
        description: 'Wire, ACH and payment-instruction traffic.',
    },
    {
        key: 'Finance',
        icon: Repeat,
        iconClass: 'bg-sky-100 text-sky-700',
        barClass: 'bg-sky-500',
        description: 'Settlements, transfers and finance ops.',
    },
    {
        key: 'Compliance',
        icon: ShieldCheck,
        iconClass: 'bg-amber-100 text-amber-700',
        barClass: 'bg-amber-500',
        description: 'KYC, AML and regulatory disclosures.',
    },
    {
        key: 'Legal',
        icon: FileText,
        iconClass: 'bg-violet-100 text-violet-700',
        barClass: 'bg-violet-500',
        description: 'Counsel correspondence and contractual notices.',
    },
    {
        key: 'General',
        icon: Mail,
        iconClass: 'bg-slate-100 text-slate-700',
        barClass: 'bg-slate-500',
        description: 'Routine inbound communications.',
    },
];
function matchesCategory(c, key) {
    return c.category === key || (c.categories || []).includes(key);
}
export function MatrixCards({ communications }) {
    const total = communications.length || 1;
    const counts = CATEGORIES.map((cat) => ({
        ...cat,
        count: communications.filter((c) => matchesCategory(c, cat.key)).length,
    }));
    const grandTotal = communications.length;
    return (_jsxs(Card, { className: "overflow-hidden rounded-2xl border border-border/60 shadow-sm", children: [_jsxs(CardHeader, { className: "flex flex-row flex-wrap items-center justify-between gap-2 border-b border-border/50 bg-muted/15 px-5 py-3.5 sm:px-6", children: [_jsx(CardTitle, { className: "text-sm font-semibold text-foreground", children: "Snapshot totals" }), _jsxs("p", { className: "text-xs text-muted-foreground", children: [_jsx("span", { className: "font-semibold tabular-nums text-foreground", children: grandTotal }), " messages"] })] }), _jsxs(CardContent, { className: "px-5 pb-6 pt-5 sm:px-6", children: [_jsx("div", { "aria-hidden": true, className: "mb-5 flex h-2 w-full overflow-hidden rounded-full bg-muted", children: counts.map((c) => c.count === 0 ? null : (_jsx(motion.div, { initial: { width: 0 }, animate: { width: `${(c.count / total) * 100}%` }, transition: { duration: 0.5, ease: 'easeOut' }, className: cn('h-full', c.barClass), title: `${c.key}: ${c.count}` }, `bar-${c.key}`))) }), _jsx("ul", { className: "grid gap-3 sm:grid-cols-2 xl:grid-cols-5", children: counts.map((c, i) => {
                            const Icon = c.icon;
                            const pct = Math.round((c.count / total) * 100);
                            return (_jsxs(motion.li, { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.04 }, className: "rounded-xl border border-border/50 bg-muted/10 p-3.5 transition-colors hover:bg-muted/20", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: cn('flex h-8 w-8 items-center justify-center rounded-lg', c.iconClass), "aria-hidden": true, children: _jsx(Icon, { className: "h-4 w-4" }) }), _jsx("p", { className: "text-sm font-semibold text-foreground", children: c.key })] }), _jsxs("div", { className: "mt-3 flex items-baseline justify-between", children: [_jsx("p", { className: "text-2xl font-bold tabular-nums tracking-tight text-foreground", children: c.count }), _jsxs("span", { className: "text-xs font-semibold text-muted-foreground", children: [pct, "%"] })] }), _jsx("div", { "aria-hidden": true, className: "mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted", children: _jsx("div", { className: cn('h-full rounded-full transition-all', c.barClass), style: { width: `${pct}%` } }) }), _jsx("p", { className: "mt-2 line-clamp-2 text-[11px] leading-snug text-muted-foreground", children: c.description })] }, c.key));
                        }) })] })] }));
}
